<script setup>
/**
 * @fileoverview SharePortalLogin - External user authentication for shared labs
 *
 * Minimal branded login page for external users to access shared labs.
 * No Okta authentication - uses Guacamole credentials created by admins.
 *
 * @example
 * Route: /share/:shareId
 * External user enters username (share-xxx@labs.autolabs.com) and password
 */

import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Lock, User, Loader2, AlertCircle } from 'lucide-vue-next'
import { useShareSession } from '@/composables/useShareSession'
import { getShareSessionToken } from '@/api/client'
import Button from '@/components/ui/button/Button.vue'
import Card from '@/components/ui/card/Card.vue'
import CardContent from '@/components/ui/card/CardContent.vue'
import CardHeader from '@/components/ui/card/CardHeader.vue'
import CardTitle from '@/components/ui/card/CardTitle.vue'
import CardDescription from '@/components/ui/card/CardDescription.vue'
import Alert from '@/components/ui/Alert.vue'
import AlertTitle from '@/components/ui/AlertTitle.vue'
import AlertDescription from '@/components/ui/AlertDescription.vue'
import Input from '@/components/ui/input/Input.vue'
import Label from '@/components/ui/label/Label.vue'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import { formatDateTime, isExpired } from '@/utils/dateFormatter'

const route = useRoute()
const router = useRouter()

// Get share ID from route params
const shareId = computed(() => route.params.shareId)

// Form state
const username = ref('')
const password = ref('')
const formError = ref(null)
const isSubmitting = ref(false)

// Share info state
const shareInfo = ref(null)
const isLoadingInfo = ref(false)
const loadError = ref(null)

// Share session composable
const { login, isAuthenticated, loadShareInfo } = useShareSession()

/**
 * Load public share information on mount
 * Displays lab name and validates share exists
 */
onMounted(async () => {
  // Check sessionStorage directly for existing session (handles page refresh/hot reload)
  // This is more reliable than checking Pinia store which may be reset
  const existingToken = getShareSessionToken()
  const existingShareId = sessionStorage.getItem('share_session_id')

  // If valid session exists for this share, redirect to VMs page
  if (existingToken && existingShareId === shareId.value) {
    router.push(`/share/${shareId.value}/vms`)
    return
  }

  // Load share info (public endpoint, no auth required)
  await loadShareInformation()
})

/**
 * Load share information from API
 */
async function loadShareInformation() {
  try {
    isLoadingInfo.value = true
    loadError.value = null

    shareInfo.value = await loadShareInfo(shareId.value)
  } catch (err) {
    console.error('Failed to load share info:', err)
    loadError.value = err.message || 'Failed to load share information'
  } finally {
    isLoadingInfo.value = false
  }
}

/**
 * Handle login form submission
 * Authenticates external user with Guacamole credentials
 */
async function handleLogin() {
  console.debug('[Login] handleLogin started at', performance.now().toFixed(0), 'ms (absolute)')

  // Reset error state
  formError.value = null

  // Validate form
  if (!username.value.trim() || !password.value.trim()) {
    formError.value = 'Please enter both username and password'
    return
  }

  try {
    isSubmitting.value = true

    // Authenticate with share portal
    console.debug('[Login] Calling login() at', performance.now().toFixed(0), 'ms')
    await login(shareId.value, username.value.trim(), password.value)
    console.debug('[Login] login() completed at', performance.now().toFixed(0), 'ms')

    // Redirect to VMs page on success
    console.debug('[Login] Calling router.push() at', performance.now().toFixed(0), 'ms')
    await router.push(`/share/${shareId.value}/vms`)
    console.debug('[Login] router.push() resolved at', performance.now().toFixed(0), 'ms')
  } catch (err) {
    console.error('Login failed:', err)
    formError.value = err.message || 'Authentication failed. Please check your credentials.'
  } finally {
    isSubmitting.value = false
  }
}

/**
 * Format expiration date for display (uses centralized utility)
 * @param {string} isoDate - ISO date string
 * @returns {string} Formatted date
 */
function formatExpirationDate(isoDate) {
  return formatDateTime(
    isoDate,
    {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    },
    'N/A'
  )
}

/**
 * Check if share is expired (uses centralized utility)
 * @param {string} isoDate - ISO date string
 * @returns {boolean} True if expired
 */
function isShareExpired(isoDate) {
  return isExpired(isoDate)
}
</script>

<template>
  <div
    class="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex flex-col items-center justify-center px-4 py-12"
  >
    <!-- Header with Branding -->
    <div class="mb-8 text-center">
      <h1 class="text-4xl font-bold text-indigo-600 dark:text-indigo-400 mb-2">Autolabs</h1>
      <p class="text-sm text-gray-600 dark:text-gray-400">Virtual Lab Platform</p>
    </div>

    <!-- Loading State -->
    <div v-if="isLoadingInfo" class="flex flex-col items-center gap-4">
      <LoadingSpinner size="lg" />
      <p class="text-sm text-gray-600 dark:text-gray-400">Loading share information...</p>
    </div>

    <!-- Error Loading Share Info -->
    <Card v-else-if="loadError" class="w-full max-w-md">
      <CardHeader>
        <CardTitle class="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertCircle class="w-5 h-5" />
          Share Not Found
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive" class="mb-4">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{{ loadError }}</AlertDescription>
        </Alert>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          This share link may be invalid, expired, or has been deleted. Please contact the person
          who shared this lab with you.
        </p>
      </CardContent>
    </Card>

    <!-- Share Expired -->
    <Card v-else-if="shareInfo && isShareExpired(shareInfo.expires_at)" class="w-full max-w-md">
      <CardHeader>
        <CardTitle class="flex items-center gap-2 text-amber-600 dark:text-amber-400">
          <AlertCircle class="w-5 h-5" />
          Share Expired
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Alert variant="destructive" class="mb-4">
          <AlertTitle>Access Expired</AlertTitle>
          <AlertDescription>
            This share link expired on {{ formatExpirationDate(shareInfo.expires_at) }}
          </AlertDescription>
        </Alert>
        <p class="text-sm text-gray-600 dark:text-gray-400">
          Please contact the lab owner to request a new share link.
        </p>
      </CardContent>
    </Card>

    <!-- Login Form -->
    <Card v-else-if="shareInfo" class="w-full max-w-md">
      <CardHeader>
        <CardTitle>Share Portal</CardTitle>
        <CardDescription> Sign in to continue </CardDescription>
      </CardHeader>

      <CardContent>
        <!-- Login Form -->
        <form @submit.prevent="handleLogin" class="space-y-4">
          <!-- Username Field -->
          <div class="space-y-2">
            <Label for="username">Username</Label>
            <div class="relative">
              <User class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="username"
                v-model="username"
                type="text"
                placeholder="share-xxx@labs.autolabs.com"
                class="pl-10"
                :disabled="isSubmitting"
                autocomplete="username"
                required
              />
            </div>
          </div>

          <!-- Password Field -->
          <div class="space-y-2">
            <Label for="password">Password</Label>
            <div class="relative">
              <Lock class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                id="password"
                v-model="password"
                type="password"
                placeholder="Enter your password"
                class="pl-10"
                :disabled="isSubmitting"
                autocomplete="current-password"
                required
              />
            </div>
          </div>

          <!-- Error Message -->
          <Alert v-if="formError" variant="destructive">
            <AlertCircle class="w-4 h-4" />
            <AlertTitle>Authentication Failed</AlertTitle>
            <AlertDescription>{{ formError }}</AlertDescription>
          </Alert>

          <!-- Submit Button -->
          <Button type="submit" class="w-full" :disabled="isSubmitting">
            <Loader2 v-if="isSubmitting" class="w-4 h-4 mr-2 animate-spin" />
            <span v-else>Sign In</span>
          </Button>
        </form>

        <!-- Footer Note -->
        <p class="mt-6 text-xs text-center text-gray-500 dark:text-gray-400">
          This is a shared lab environment. Use the credentials provided by the lab owner.
        </p>
      </CardContent>
    </Card>
  </div>
</template>

<style scoped>
/* Gradient background animation */
@keyframes gradient {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.bg-gradient-to-br {
  background-size: 200% 200%;
  animation: gradient 15s ease infinite;
}
</style>
